/**
 * ClientOnboardingBillingReporting Component
 * Collects billing and reporting contact information
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import { useBeforeUnload } from '../../hooks';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboarding.module.css';

const ClientOnboardingBillingReporting = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [billingContact, setBillingContact] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: ''
    });

    const [reportingContact, setReportingContact] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: ''
    });

    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(false);
    const [reportingSameAsBillingAddress, setReportingSameAsBillingAddress] = useState(false);
    const [additionalContactsUsePreviousAddress, setAdditionalContactsUsePreviousAddress] = useState({});
    const [businessAddress, setBusinessAddress] = useState(null);
    const [additionalContacts, setAdditionalContacts] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSaved, setIsSaved] = useState(true);
    const [initialData, setInitialData] = useState(null);

    // Load saved data on mount
    useEffect(() => {
        const loadSavedData = async () => {
            // Load business address from previous step
            try {
                const progress = await publicTokenService.getProgress(token);
                const actualProgress = progress.progress || progress;
                
                if (actualProgress && actualProgress.stepData && actualProgress.stepData.step3) {
                    const businessInfo = actualProgress.stepData.step3;
                    setBusinessAddress({
                        address1: businessInfo.address || '',
                        address2: businessInfo.address2 || '',
                        city: businessInfo.city || '',
                        state: businessInfo.state || '',
                        zipCode: businessInfo.zipCode || ''
                    });
                }
                
                if (actualProgress && actualProgress.stepData && actualProgress.stepData.step4) {
                    const data = actualProgress.stepData.step4;
                    setBillingContact(data.billingContact || billingContact);
                    setReportingContact(data.reportingContact || reportingContact);
                    setSameAsBilling(data.sameAsBilling || false);
                    setSameAsBusinessAddress(data.sameAsBusinessAddress || false);
                    setReportingSameAsBillingAddress(data.reportingSameAsBillingAddress || false);
                    setAdditionalContactsUsePreviousAddress(data.additionalContactsUsePreviousAddress || {});
                    setAdditionalContacts(data.additionalContacts || []);
                    setInitialData(JSON.stringify(data));
                    return;
                }
            } catch (error) {
                // Fallback to sessionStorage
            }
            
            // Fallback: try to load business address from sessionStorage
            if (!businessAddress) {
                const savedBusinessInfo = sessionStorage.getItem(`onboarding_business_info_${token}`);
                if (savedBusinessInfo) {
                    const businessInfo = JSON.parse(savedBusinessInfo);
                    setBusinessAddress({
                        address1: businessInfo.address || '',
                        address2: businessInfo.address2 || '',
                        city: businessInfo.city || '',
                        state: businessInfo.state || '',
                        zipCode: businessInfo.zipCode || ''
                    });
                }
            }
            
            // Fallback to sessionStorage for contacts
            const saved = sessionStorage.getItem(`onboarding_contacts_${token}`);
            if (saved) {
                const parsedData = JSON.parse(saved);
                setBillingContact(parsedData.billingContact || billingContact);
                setReportingContact(parsedData.reportingContact || reportingContact);
                setSameAsBilling(parsedData.sameAsBilling || false);
                setSameAsBusinessAddress(parsedData.sameAsBusinessAddress || false);
                setReportingSameAsBillingAddress(parsedData.reportingSameAsBillingAddress || false);
                setAdditionalContactsUsePreviousAddress(parsedData.additionalContactsUsePreviousAddress || {});
                setAdditionalContacts(parsedData.additionalContacts || []);
                setInitialData(saved);
            } else {
                setInitialData(JSON.stringify({ billingContact, reportingContact, sameAsBilling, sameAsBusinessAddress, reportingSameAsBillingAddress, additionalContactsUsePreviousAddress, additionalContacts }));
            }
        };
        
        loadSavedData();
    }, [token]);

    // Track unsaved changes
    useEffect(() => {
        if (initialData) {
            const currentData = JSON.stringify({ billingContact, reportingContact, sameAsBilling, sameAsBusinessAddress, reportingSameAsBillingAddress, additionalContactsUsePreviousAddress, additionalContacts });
            setIsSaved(currentData === initialData);
        }
    }, [billingContact, reportingContact, sameAsBilling, sameAsBusinessAddress, reportingSameAsBillingAddress, additionalContactsUsePreviousAddress, additionalContacts, initialData]);

    // Warn before leaving if unsaved changes
    useBeforeUnload(!isSaved, 'You have unsaved changes. Please save your progress before leaving.');

    const US_STATES = [
        'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
        'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
        'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
        'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    const handleBillingChange = (field, value) => {
        setBillingContact(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[`billing_${field}`]) {
            setErrors(prev => ({ ...prev, [`billing_${field}`]: '' }));
        }
        
        // If "same as billing" is checked, update reporting contact too
        if (sameAsBilling) {
            setReportingContact(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleReportingChange = (field, value) => {
        setReportingContact(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[`reporting_${field}`]) {
            setErrors(prev => ({ ...prev, [`reporting_${field}`]: '' }));
        }
    };

    const handleSameAsBillingChange = (checked) => {
        setSameAsBilling(checked);
        if (checked) {
            setReportingContact({ ...billingContact });
        }
    };

    const handleReportingSameAsBillingAddressChange = (checked) => {
        setReportingSameAsBillingAddress(checked);
        if (checked) {
            setReportingContact(prev => ({
                ...prev,
                address1: billingContact.address1,
                address2: billingContact.address2,
                city: billingContact.city,
                state: billingContact.state,
                zipCode: billingContact.zipCode
            }));
        }
    };

    const handleSameAsBusinessAddressChange = (checked) => {
        setSameAsBusinessAddress(checked);
        if (checked && businessAddress) {
            setBillingContact(prev => ({
                ...prev,
                address1: businessAddress.address1,
                address2: businessAddress.address2,
                city: businessAddress.city,
                state: businessAddress.state,
                zipCode: businessAddress.zipCode
            }));
            
            // If reporting is same as billing, update reporting too
            if (sameAsBilling) {
                setReportingContact(prev => ({
                    ...prev,
                    address1: businessAddress.address1,
                    address2: businessAddress.address2,
                    city: businessAddress.city,
                    state: businessAddress.state,
                    zipCode: businessAddress.zipCode
                }));
            }
        }
    };

    const handleAddContact = () => {
        setAdditionalContacts(prev => [...prev, {
            id: Date.now(),
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipCode: '',
            isBilling: false,
            isReporting: false
        }]);
    };

    const handleRemoveContact = (id) => {
        setAdditionalContacts(prev => prev.filter(contact => contact.id !== id));
        // Clear the state for this contact's address checkbox
        setAdditionalContactsUsePreviousAddress(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
        // Clear all errors related to additional contacts since indices will shift
        setErrors(prev => {
            const newErrors = { ...prev };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith('additional_')) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const handleAdditionalContactUsePreviousAddress = (id, checked, index) => {
        setAdditionalContactsUsePreviousAddress(prev => ({
            ...prev,
            [id]: checked
        }));
        
        if (checked) {
            // Determine which contact to get address from
            let sourceContact;
            if (index === 0) {
                // First additional contact uses reporting contact
                sourceContact = reportingContact;
            } else {
                // Subsequent additional contacts use the previous additional contact
                sourceContact = additionalContacts[index - 1];
            }
            
            // Update the additional contact's address
            setAdditionalContacts(prev => prev.map(contact => 
                contact.id === id ? {
                    ...contact,
                    address1: sourceContact.address1,
                    address2: sourceContact.address2,
                    city: sourceContact.city,
                    state: sourceContact.state,
                    zipCode: sourceContact.zipCode
                } : contact
            ));
        }
    };

    const handleAdditionalContactChange = (id, field, value) => {
        // Find the index BEFORE updating state
        const index = additionalContacts.findIndex(contact => contact.id === id);
        
        setAdditionalContacts(prev => prev.map(contact => 
            contact.id === id ? { ...contact, [field]: value } : contact
        ));
        
        // Clear the corresponding error for this field
        if (index !== -1) {
            setErrors(prev => {
                const newErrors = { ...prev };
                const errorKey = `additional_${index}_${field}`;
                if (newErrors[errorKey]) {
                    delete newErrors[errorKey];
                }
                // Also clear the type error if checking a checkbox
                if (field === 'isBilling' || field === 'isReporting') {
                    const typeErrorKey = `additional_${id}_type`;
                    if (newErrors[typeErrorKey]) {
                        delete newErrors[typeErrorKey];
                    }
                }
                return newErrors;
            });
        }
    };

    const validateContact = (contact, prefix) => {
        const contactErrors = {};
        
        if (!(contact.firstName || '').trim()) {
            contactErrors[`${prefix}_firstName`] = 'First name is required';
        }
        
        if (!(contact.lastName || '').trim()) {
            contactErrors[`${prefix}_lastName`] = 'Last name is required';
        }
        
        if (!(contact.email || '').trim()) {
            contactErrors[`${prefix}_email`] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
            contactErrors[`${prefix}_email`] = 'Invalid email format';
        }
        
        if (!(contact.phone || '').trim()) {
            contactErrors[`${prefix}_phone`] = 'Phone is required';
        } else if (!/^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/.test(contact.phone)) {
            contactErrors[`${prefix}_phone`] = 'Invalid phone format (e.g., 123-456-7890)';
        }
        
        if (!(contact.address1 || '').trim()) {
            contactErrors[`${prefix}_address1`] = 'Address is required';
        }
        
        if (!(contact.city || '').trim()) {
            contactErrors[`${prefix}_city`] = 'City is required';
        }
        
        if (!(contact.state || '').trim()) {
            contactErrors[`${prefix}_state`] = 'State is required';
        }
        
        if (!(contact.zipCode || '').trim()) {
            contactErrors[`${prefix}_zipCode`] = 'ZIP code is required';
        } else if (!/^\d{5}(-\d{4})?$/.test(contact.zipCode)) {
            contactErrors[`${prefix}_zipCode`] = 'Invalid ZIP code format';
        }
        
        return contactErrors;
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Validate billing contact
        Object.assign(newErrors, validateContact(billingContact, 'billing'));
        
        // Validate reporting contact (unless same as billing)
        if (!sameAsBilling) {
            Object.assign(newErrors, validateContact(reportingContact, 'reporting'));
        }
        
        // Validate additional contacts
        additionalContacts.forEach((contact, index) => {
            if (!contact.isBilling && !contact.isReporting) {
                newErrors[`additional_${contact.id}_type`] = 'Select at least billing or reporting';
            }
            Object.assign(newErrors, validateContact(contact, `additional_${index}`));
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveAndExit = async () => {
        try {
            // Save current data without validation
            const contactsData = {
                billingContact,
                reportingContact,
                additionalContacts,
                sameAsBilling,
                sameAsBusinessAddress,
                reportingSameAsBillingAddress,
                additionalContactsUsePreviousAddress
            };
            
            // Save to backend (step 4: Billing & Reporting Contacts)
            await publicTokenService.saveProgress(token, {
                stepNumber: 4,
                stepData: contactsData,
                markStepComplete: false,
                moveToNextStep: false
            });
            
            sessionStorage.setItem(`onboarding_contacts_${token}`, JSON.stringify(contactsData));
            setIsSaved(true);
            setInitialData(JSON.stringify(contactsData));
            alert('Your progress has been saved. You can return using the same link to continue.');
        } catch (error) {
            console.error('Failed to save progress:', error);
            alert('Failed to save progress. Please try again.');
        }
    };

    const handleContinue = async () => {
        if (validateForm()) {
            try {
                // Save contacts to session storage
                const contactsData = {
                    billingContact,
                    reportingContact,
                    additionalContacts,
                    sameAsBilling,
                    sameAsBusinessAddress,
                    reportingSameAsBillingAddress,
                    additionalContactsUsePreviousAddress
                };
                
                // Save to backend and mark step complete (step 4: Billing & Reporting Contacts)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 4,
                    stepData: contactsData,
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                sessionStorage.setItem(`onboarding_contacts_${token}`, JSON.stringify(contactsData));
                setIsSaved(true);
                
                // Navigate to reporting requirements
                navigate(`/client-onboarding/${token}/reporting-requirements`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleGoBack = () => {
        const accountType = sessionStorage.getItem(`onboarding_account_type_${token}`);
        if (accountType === 'business') {
            navigate(`/client-onboarding/${token}/business-info`);
        } else {
            navigate(`/client-onboarding/${token}/account-type`);
        }
    };

    const renderContactForm = (contact, handleChange, prefix, title, description = null, showTypeSelection = false, showBusinessAddressCheckbox = false, showBillingAddressCheckbox = false, contactId = null, contactIndex = null) => (
        <div className={styles.contactSection}>
            {showTypeSelection && contactId !== null ? (
                <div className={styles.contactHeader}>
                    <h3 className={styles.contactTitle}>{title}</h3>
                    <button
                        type="button"
                        onClick={() => handleRemoveContact(contactId)}
                        className={styles.removeButton}
                    >
                        ✕ Remove
                    </button>
                </div>
            ) : (
                <>
                    <h3 className={styles.contactTitle}>{title}</h3>
                    {description && (
                        <p className={styles.contactDescription}>{description}</p>
                    )}
                </>
            )}
            
            {showTypeSelection && (
                <div className={styles.contactTypeSection}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={contact.isBilling}
                            onChange={(e) => handleChange(contact.id, 'isBilling', e.target.checked)}
                            className={styles.checkbox}
                        />
                        <span>Billing Contact</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={contact.isReporting}
                            onChange={(e) => handleChange(contact.id, 'isReporting', e.target.checked)}
                            className={styles.checkbox}
                        />
                        <span>Reporting Contact</span>
                    </label>
                    {errors[`additional_${contact.id}_type`] && (
                        <span className={styles.errorText}>{errors[`additional_${contact.id}_type`]}</span>
                    )}
                </div>
            )}
            
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        First Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={contact.firstName}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'firstName', e.target.value) : handleChange('firstName', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_firstName`] ? styles.inputError : ''}`}
                        placeholder="First name"
                    />
                    {errors[`${prefix}_firstName`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_firstName`]}</span>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Last Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={contact.lastName}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'lastName', e.target.value) : handleChange('lastName', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_lastName`] ? styles.inputError : ''}`}
                        placeholder="Last name"
                    />
                    {errors[`${prefix}_lastName`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_lastName`]}</span>
                    )}
                </div>
            </div>

            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Email <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'email', e.target.value) : handleChange('email', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_email`] ? styles.inputError : ''}`}
                        placeholder="email@example.com"
                    />
                    {errors[`${prefix}_email`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_email`]}</span>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Phone <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'phone', e.target.value) : handleChange('phone', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_phone`] ? styles.inputError : ''}`}
                        placeholder="123-456-7890"
                    />
                    {errors[`${prefix}_phone`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_phone`]}</span>
                    )}
                </div>
            </div>

            {showBusinessAddressCheckbox && businessAddress && (
                <div className={styles.sameAsBillingSection} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    <label className={styles.checkboxLabelLarge}>
                        <input
                            type="checkbox"
                            checked={sameAsBusinessAddress}
                            onChange={(e) => handleSameAsBusinessAddressChange(e.target.checked)}
                            className={styles.checkbox}
                        />
                        <span>Same Physical Address as {sessionStorage.getItem(`onboarding_account_type_${token}`) === 'individual' ? 'Client' : 'Business'}</span>
                    </label>
                </div>
            )}

            {showBillingAddressCheckbox && (
                <div className={styles.sameAsBillingSection} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    <label className={styles.checkboxLabelLarge}>
                        <input
                            type="checkbox"
                            checked={reportingSameAsBillingAddress}
                            onChange={(e) => handleReportingSameAsBillingAddressChange(e.target.checked)}
                            className={styles.checkbox}
                        />
                        <span>Same Address as Previous</span>
                    </label>
                </div>
            )}

            {showTypeSelection && contactId !== null && contactIndex !== null && (
                <div className={styles.sameAsBillingSection} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                    <label className={styles.checkboxLabelLarge}>
                        <input
                            type="checkbox"
                            checked={additionalContactsUsePreviousAddress[contactId] || false}
                            onChange={(e) => handleAdditionalContactUsePreviousAddress(contactId, e.target.checked, contactIndex)}
                            className={styles.checkbox}
                        />
                        <span>Same Address as Previous</span>
                    </label>
                </div>
            )}

            <div className={styles.formGroup}>
                <label className={styles.label}>
                    Address Line 1 <span className={styles.required}>*</span>
                </label>
                <input
                    type="text"
                    value={contact.address1}
                    onChange={(e) => showTypeSelection ? handleChange(contact.id, 'address1', e.target.value) : handleChange('address1', e.target.value)}
                    className={`${styles.input} ${errors[`${prefix}_address1`] ? styles.inputError : ''}`}
                    placeholder="Street address"
                />
                {errors[`${prefix}_address1`] && (
                    <span className={styles.errorText}>{errors[`${prefix}_address1`]}</span>
                )}
            </div>

            <div className={styles.formGroup}>
                <label className={styles.label}>Address Line 2</label>
                <input
                    type="text"
                    value={contact.address2}
                    onChange={(e) => showTypeSelection ? handleChange(contact.id, 'address2', e.target.value) : handleChange('address2', e.target.value)}
                    className={styles.input}
                    placeholder="Apartment, suite, etc. (optional)"
                />
            </div>

            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        City <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={contact.city}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'city', e.target.value) : handleChange('city', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_city`] ? styles.inputError : ''}`}
                        placeholder="City"
                    />
                    {errors[`${prefix}_city`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_city`]}</span>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        State <span className={styles.required}>*</span>
                    </label>
                    <select
                        value={contact.state}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'state', e.target.value) : handleChange('state', e.target.value)}
                        className={`${styles.select} ${errors[`${prefix}_state`] ? styles.inputError : ''}`}
                    >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                        ))}
                    </select>
                    {errors[`${prefix}_state`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_state`]}</span>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        ZIP Code <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={contact.zipCode}
                        onChange={(e) => showTypeSelection ? handleChange(contact.id, 'zipCode', e.target.value) : handleChange('zipCode', e.target.value)}
                        className={`${styles.input} ${errors[`${prefix}_zipCode`] ? styles.inputError : ''}`}
                        placeholder="12345"
                        maxLength="10"
                    />
                    {errors[`${prefix}_zipCode`] && (
                        <span className={styles.errorText}>{errors[`${prefix}_zipCode`]}</span>
                    )}
                </div>
            </div>

            {showTypeSelection && (
                <button
                    type="button"
                    onClick={() => handleRemoveContact(contact.id)}
                    className={styles.removeButton}
                >
                    Remove Contact
                </button>
            )}
        </div>
    );

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>Billing and Reporting Contacts</h1>
                            <p className={styles.subtitle}>
                                Both contacts are required but can be the same person. Additional contacts are optional.
                            </p>
                        </div>

                        <form className={styles.form}>
                            {/* Same as Billing Checkbox Tile */}
                            <div className={styles.sameAsBillingTile}>
                                <div className={styles.sameAsBillingSection}>
                                    <label className={styles.checkboxLabelLarge}>
                                        <input
                                            type="checkbox"
                                            checked={sameAsBilling}
                                            onChange={(e) => handleSameAsBillingChange(e.target.checked)}
                                            className={styles.checkbox}
                                        />
                                        <span>Primary Billing and Reporting contact will be the same person</span>
                                    </label>
                                </div>
                            </div>

                            {/* Billing and Reporting Contacts Grid */}
                            <div className={styles.contactsGrid}>
                                {/* Billing Contact */}
                                {renderContactForm(
                                    billingContact,
                                    handleBillingChange,
                                    'billing',
                                    '📧 Billing Contact',
                                    'Authorized to make financial decisions and will receive invoices.',
                                    false,
                                    true
                                )}

                                {/* Reporting Contact */}
                                {!sameAsBilling && renderContactForm(
                                    reportingContact,
                                    handleReportingChange,
                                    'reporting',
                                    '📊 Reporting Contact',
                                    'Will receive analytical results and lab reports.',
                                    false,
                                    false,
                                    true
                                )}
                            </div>

                            {/* Additional Contacts */}
                            {additionalContacts.length > 0 && (
                                <div className={styles.additionalContactsSection}>
                                    <h2 className={styles.sectionTitle}>Additional Contacts</h2>
                                    <div className={styles.additionalContactsGrid}>
                                        {additionalContacts.map((contact, index) => (
                                            <div key={contact.id}>
                                            {renderContactForm(
                                                contact,
                                                handleAdditionalContactChange,
                                                `additional_${index}`,
                                                `Additional Contact ${index + 1}`,
                                                null,
                                                true,
                                                false,
                                                false,
                                                contact.id,
                                                index
                                            )}
                                        </div>
                                    ))}
                                    </div>
                                </div>
                            )}

                            {/* Add Contact Button */}
                            <div className={styles.addContactSection}>
                                <button
                                    type="button"
                                    onClick={handleAddContact}
                                    className={styles.addContactButton}
                                >
                                    + Add Additional Contact
                                </button>
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    onClick={handleGoBack}
                                    className={styles.backButton}
                                >
                                    ← Go Back
                                </button>
                                <div className={styles.rightActions}>
                                    <button
                                        type="button"
                                        onClick={handleSaveAndExit}
                                        className={styles.saveButton}
                                    >
                                        💾 Save and Continue Later
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleContinue}
                                        className={styles.continueButton}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingBillingReporting;
